import {
	CarriersEnum,
	ContentTypeEnum,
	CurrencyEnum,
	DimensionTypeEnum,
	EELPFCEnum,
	LiveAnimalTypeEnum,
	NonDeliveryOptionEnum,
	PackageTypeEnum,
	RatingTypeEnum,
	ReprintLabelPackageSelectionEnum,
	RestrictionTypesEnum,
	ServiceEnum,
	ShipmentManifestStatusEnum,
	ShipmentRateStatusEnum,
	ShippingStatusEnum,
	SpecialServiceExternalEnum,
	WeightUnitEnum,
} from '@app/app.enum';
import { listEndDate, listStartDate } from '@app/constants';
import * as models from '@app/models/index';
import { InjectedIntl } from 'react-intl';
import { IAccount, IAddress } from './account.models';

export interface IShipmentComponentProps extends models.ISnackBarFeature, models.IAxiosCancellation {
	intl: InjectedIntl;
}

export interface IShipmentComponentState extends models.IPagedResult<models.IShipment> {
	currentPage: number;
	pageJump: number;
	disablePageJump: boolean;
	startDate: Date;
	endDate: Date;
	collapse: Record<string, boolean>;
	selectShipment: {};
	disableDatePickerFilter: boolean;
	dataLoading: boolean;
	appId?: string;
	accountId?: string;
	filterPanelExpanded: boolean;
	isOpenCarrierCodeFilter: boolean;
	carrierAccountId: string;
	trackingNumber: string;
}

export interface IShipmentDetailProps extends models.ISnackBarFeature, models.IAxiosCancellation {
	intl: InjectedIntl;
	shipment: models.IShipment;
	style: React.CSSProperties;
	onShipmentUpdate?: (shipment: models.IShipment) => void;
}

export interface IShipmentDetailState {
	isOpenRefundStatusDialog: boolean;
	isOpenReprintLabelDialog: boolean;
	reprintLabelQuantity: ReprintLabelPackageSelectionEnum;
	reprintPackageNumber: number;
	isShipmentRefundLoading: boolean;
	isReprintLabelLoading: boolean;
	packageCurrentPage: number;
	packageNumber: number;
	packageLoading: boolean;
	totalPackages: number;
	shipment: models.IShipment;
	multiPieceShipments: models.IMultiPieceShipment[];
	isIncludeOtherClientFeeCharges: boolean;
	isIncludeOtherPartnerFeeCharges: boolean;
}

export const initialShipmentComponentState = (): IShipmentComponentState => ({
	results: [],
	nextPage: null,
	previousPage: null,
	pageSize: 0,
	totalPages: 0,
	totalResults: 0,
	currentPage: 1,
	pageJump: 1,
	disablePageJump: false,
	startDate: listStartDate.toDate(),
	endDate: listEndDate.toDate(),
	collapse: {},
	selectShipment: {},
	disableDatePickerFilter: false,
	dataLoading: false,
	appId: '',
	filterPanelExpanded: false,
	isOpenCarrierCodeFilter: false,
	carrierAccountId: '',
	trackingNumber: '',
});

export interface IShipment {
	id: string;
	accountId: string;
	toAddress: IAddress;
	fromAddress: IAddress;
	packages: IPackage[];
	rates?: IShipmentRates[];
	manifestId: string;
	postageLabel: IPostageLabel;
	tracking: IShipmentTracking;
	refundStatus?: string;
	returnLabelUrl?: string;
	service: ServiceEnum;
	specialService: SpecialServiceExternalEnum[];
	liveAnimalType?: LiveAnimalTypeEnum;
	insuredAmount?: number;
	customsInfo?: ICustomsInfo;
	errors?: string[];
	createdDate: Date;
	transactionId: number;
	appId?: string;
	account?: IAccount;
	carrierAccountName?: string;
	carrierCode?: string;
	shipDate?: Date;
	option?: any;
	shipmentManifestStatus?: string;
}

export interface IShipmentManifestReportVM {
	trackingNumber: string;
	shipDate?: Date;
	shipmentManifestStatus: ShipmentManifestStatusEnum;
	shipmentId: string;
	downloadUrl: string;
	carrierCode: CarriersEnum;
	accountName: string;
}

export interface IPackage {
	id: string;
	accountId: string;
	packageType: PackageTypeEnum;
	isRectangle: boolean;
	length: number;
	width: number;
	height: number;
	dimensionUnit: DimensionTypeEnum;
	weight: number;
	weightUnit: WeightUnitEnum;
	packageNumber: number;
	isMaster: boolean;
}

export interface IMultiPieceShipment {
	package: models.IPackage;
	rates?: models.IShipmentRates[];
	postageLabel: IPostageLabel;
	tracking: models.IShipmentTracking;
	totalPackages: number;
	shipmentId: string;
	accountId: string;
	manifestId?: string;
}

export interface IPostageLabel {
	labelFileType: string;
	labelOrientation: string;
	labelSize: string;
	labels: string[];
	labelType: string;
	shipDate: string;
}

export interface IReprintLabel {
	labelFileType: string;
	labelSize: string;
	labelOrientation: string;
	labels: string[];
	labelType: string;
}

export interface IShipmentRates {
	carrierCode?: CarriersEnum;
	currency?: CurrencyEnum;
	deliveryDate?: Date;
	deliveryDateGuaranteed?: boolean;
	deliveryDays?: number;
	finalWeightUnit?: WeightUnitEnum;
	finalDimensionUnit?: DimensionTypeEnum;
	finalPackageType?: PackageTypeEnum;
	finalService?: ServiceEnum;
	finalVolume?: number;
	finalWeight?: number;
	id?: string;
	overSized?: boolean;
	fees: IFees[];
	status?: ShipmentRateStatusEnum;
	surCharge?: number;
	totalFee?: number;
	type?: RatingTypeEnum;
	zone?: string;
}
export interface IFees {
	packageNumber: number;
	type: string;
	fee: number;
	feeIncludingOtherCharges?: number;
}
export interface IShipmentTracking {
	status?: ShippingStatusEnum;
	subStatus?: string;
	trackingNumber?: string;
	trackingBarcodeNumber?: string;
	additionalTrackingNumber?: string;
	additionalTrackingBarcodeNumber?: string;
	trackingUrl?: string;
}

export interface ITrackingDetails {
	message?: string;
	dateTime?: Date;
	status?: ShippingStatusEnum;
	city?: string;
	state?: string;
	country?: string;
	zip?: string;
}

export interface ICustomsInfo {
	id?: string;
	customsItem?: ICustomsItem[];
	customsItemId?: string[];
	contentType?: ContentTypeEnum;
	contentsExplanation?: string;
	customsCertify?: boolean;
	customsSigner?: string;
	nonDeliveryOption?: NonDeliveryOptionEnum;
	restrictionType?: RestrictionTypesEnum;
	restrictionComments?: string;
	eelpfc?: EELPFCEnum;
	accountId?: string;
}

export interface ICustomsItem {
	weightUnit?: WeightUnitEnum;
	weightInOtherUnit?: IPackageWeight;
	carrierSpecificCountryCode?: string;
	customsInfoId?: string;
	description?: string;
	quantity?: number;
	weight?: number;
	value?: number;
	hsTariffNumber?: string;
	originCountry?: string;
}

export interface IPackageWeight {
	weightPounds?: number;
	weightOunces?: number;
	weight?: number;
}



// WEBPACK FOOTER //
// ./src/models/shipment.models.ts